import { forwardRef, HTMLProps, Ref } from "react";
import clsx from "clsx";

import { FilePlusSvg } from "@components/Svg";
import { Loader } from "@components/Loader";

interface InputProps extends HTMLProps<HTMLInputElement> {
  name: string;
  label?: string;
  disabled?: boolean;
  uploading?: boolean;
  error?: string;
  dataTestid?: string;
}

export const FormFile = forwardRef(
  (
    {
      name,
      label = "Receipts",
      disabled = false,
      uploading = false,
      error,
      dataTestid,
      ...inputProps
    }: InputProps,
    ref: Ref<HTMLInputElement>,
  ) => (
    <div className={inputProps.className}>
      <label
        className={clsx(
          "relative",
          (disabled || uploading) && "pointer-events-none",
        )}
        data-testid={dataTestid}
      >
        <input
          {...inputProps}
          onDragOver={(event) => event.preventDefault()}
          onDrag={inputProps.onChange}
          type="file"
          multiple
          ref={ref}
          id={`${name}__input`}
          placeholder=""
          className="absolute h-full w-full opacity-0"
          disabled={disabled}
        />
        <div
          className={clsx(
            "flex h-16 w-full cursor-pointer items-center justify-between rounded-lg border border-dashed px-5 transition duration-300",
            disabled || uploading
              ? "border-black-54 bg-black-4 opacity-55"
              : "border-primary-300 bg-primary-104",
            !!error && "border-red-100",
          )}
        >
          <div
            className={clsx(
              "flex items-center gap-x-1.5 text-300 font-bold *:size-[28px]",
              disabled || uploading
                ? "text-black-54 *:text-black-32"
                : "text-black-100 *:text-primary-300",
            )}
          >
            <FilePlusSvg />
            {label}
          </div>
          <div
            className={clsx(
              "font-light text-black-54 *:font-bold",
              disabled || uploading ? "*:text-black-54" : "*:text-primary-300",
            )}
          >
            <span>Browse</span>
          </div>
        </div>
        {uploading && <Loader />}
      </label>
      {error && (
        <p
          className={clsx(
            "pl-4 pt-1 text-100 text-black-54",
            error && "text-red-100",
          )}
        >
          {error}
        </p>
      )}
    </div>
  ),
);
